/**
 * Inspection photo helpers — the single definition of "is this entry a photo URL?".
 *
 * `bulkInspectionMaterials` is a DUAL-PURPOSE array: the mobile app stores photo URLs there,
 * while the web bulk-inspection form also stores free-text material tag names ("Краска") in the
 * same array. Rendering the raw array as <img> produced broken thumbnails; the predicate used to
 * be copy-pasted into every consumer and one of them always forgot it.
 *
 * Mirrors the backend `PtoInspectionPhotos.IsFileUrlLike` / `.Key`.
 */

/** True for a photo/file URL, false for a material tag. */
export function isPhotoUrl(value: string): boolean {
  return value.startsWith('/') || value.startsWith('http');
}

/**
 * Comparison key for one photo: the file id from the LAST `/files/` segment, so `/api/files/{id}`,
 * `/api/lk/files/{id}`, `/api/mobile/files/{id}` and absolute-host legacy forms all collapse to the
 * same value. Query/fragment stripped. Anything else falls back to the trimmed URL.
 */
export function photoKey(url: string): string {
  const marker = '/files/';
  const index = url.toLowerCase().lastIndexOf(marker);
  if (index >= 0) {
    const tail = url.slice(index + marker.length).split(/[?#]/)[0].replace(/^\/+|\/+$/g, '');
    if (tail.length > 0) return tail.toLowerCase();
  }
  return url.trim().toLowerCase();
}

/** Deduplicates photo URLs by {@link photoKey}; first occurrence wins, order preserved. */
export function dedupePhotos(urls: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const url of urls) {
    if (!url) continue;
    const key = photoKey(url);
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(url);
  }
  return result;
}
